import React, { useState, useCallback, useEffect } from 'react';
import { alarmAPI } from '../../../services/api';
import AlarmModal from './AlarmModal';
import './AlarmDropdown.css';

const AlarmDropdown = ({ user, onClose }) => {
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedAlarm, setSelectedAlarm] = useState(null);

  // 알람 목록 조회
  const fetchAlarms = useCallback(async (cursor = null) => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const condition = {
        userId: user.id,
        cursor: cursor,
        limit: 10
      };
      
      const response = await alarmAPI.getAll(condition);
      
      if (cursor === null) {
        // 첫 로드
        setAlarms(response.alarms || []);
      } else {
        // 추가 로드
        setAlarms(prev => [...prev, ...(response.alarms || [])]);
      }
      
      setNextCursor(response.nextCursor || null);
      setHasNext(response.hasNext || false);
      setTotalCount(response.totalCount || 0);
    } catch (error) {
      console.error('알람 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // 초기 로드
  useEffect(() => {
    fetchAlarms();
  }, [fetchAlarms]);

  // 알람 읽음 처리
  const handleMarkAsRead = async (alarmId, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    try {
      await alarmAPI.markAsRead(alarmId);
      
      // 로컬 상태 업데이트
      setAlarms(prev => 
        prev.map(alarm => 
          alarm.id === alarmId ? { ...alarm, isRead: true } : alarm
        )
      );
    } catch (error) {
      console.error('알람 읽음 처리 실패:', error);
    }
  };

  // 읽지 않은 알람 클릭 시 알럿창 표시
  const handleAlarmClick = (alarm) => {
    if (!alarm.isRead) {
      setSelectedAlarm(alarm);
    }
  };

  // 알럿창 닫기
  const handleCloseAlert = () => {
    setSelectedAlarm(null);
  };

  // 알럿창에서 읽음 처리
  const handleAlertMarkAsRead = (alarmId) => {
    handleMarkAsRead(alarmId);
    setSelectedAlarm(null);
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const diffMinutes = Math.floor(diff / 60000);
    const diffHours = Math.floor(diff / 3600000);
    const diffDays = Math.floor(diff / 86400000);

    if (diffMinutes < 1) return '방금 전';
    if (diffMinutes < 60) return `${diffMinutes}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;
    
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 더 보기 버튼 클릭
  const handleLoadMore = () => {
    if (hasNext && nextCursor) {
      fetchAlarms(nextCursor);
    }
  };

  return (
    <div className="alarm-dropdown">
      <div className="alarm-dropdown-header">
        <h3 className="alarm-dropdown-title">알람</h3>
        {totalCount > 0 && (
          <span className="alarm-dropdown-count">{totalCount}</span>
        )}
        <button className="alarm-dropdown-close" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="alarm-dropdown-list">
        {alarms.length > 0 ? (
          alarms.map((alarm) => (
            <div 
              key={alarm.id} 
              className={`alarm-dropdown-item ${alarm.isRead ? 'read' : 'unread'}`}
              onClick={() => handleAlarmClick(alarm)}
              style={{ cursor: !alarm.isRead ? 'pointer' : 'default' }}
            >
              <div className="alarm-dropdown-content">
                <div className="alarm-dropdown-header-row">
                  <h4 className="alarm-dropdown-item-title">{alarm.title}</h4>
                  {!alarm.isRead && (
                    <button
                      className="alarm-dropdown-read-button"
                      onClick={(e) => handleMarkAsRead(alarm.id, e)}
                      title="읽음 처리"
                    >
                      읽음
                    </button>
                  )}
                </div>
                <p className="alarm-dropdown-item-content">{alarm.content}</p>
                <div className="alarm-dropdown-meta">
                  <span className="alarm-dropdown-date">{formatDate(alarm.createdAt)}</span>
                  {!alarm.isRead && <span className="alarm-dropdown-badge">NEW</span>}
                </div>
              </div>
            </div>
          ))
        ) : (
          !loading && (
            <div className="alarm-dropdown-empty">
              알람이 없습니다.
            </div>
          )
        )}
      </div>

      {loading && (
        <div className="alarm-dropdown-loading">
          로딩 중...
        </div>
      )}

      {hasNext && !loading && (
        <div className="alarm-dropdown-load-more">
          <button className="alarm-dropdown-load-more-button" onClick={handleLoadMore}>
            더 보기
          </button>
        </div>
      )}

      {/* 읽지 않은 알람 알럿창 */}
      {selectedAlarm && (
        <AlarmModal
          alarm={selectedAlarm}
          onClose={handleCloseAlert}
          onMarkAsRead={handleAlertMarkAsRead}
        />
      )}
    </div>
  );
};

export default AlarmDropdown;

